import React, { useState, useEffect } from 'react';
import { getDashboardStats } from '../lib/api';
import { DASHBOARD } from '../constants/testIds';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users,
  UserCircle,
  DollarSign,
  Calendar,
  TrendingUp,
  Clock,
  Plane,
  FileText,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

const StatCard = ({ title, value, icon: Icon, subtitle, trend, testId }) => (
  <Card data-testid={testId} className="border-border hover:shadow-md transition-all duration-200 hover:-translate-y-1">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">{title}</p>
          <p className="text-3xl font-bold text-foreground font-outfit">{value}</p>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#383838' }}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      {trend && (
        <div className="mt-3 flex items-center gap-1 text-sm">
          <TrendingUp className="w-4 h-4 text-emerald-600" />
          <span className="text-emerald-600 font-medium">{trend}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const getStatusClass = (status) => {
  const classes = {
    new: 'bg-blue-50 text-blue-700',
    contacted: 'bg-purple-50 text-purple-700',
    qualified: 'bg-cyan-50 text-cyan-700',
    proposal: 'bg-amber-50 text-amber-700',
    won: 'bg-emerald-50 text-emerald-700',
    lost: 'bg-red-50 text-red-700',
  };
  return classes[status] || 'bg-slate-100 text-slate-700';
};

import { formatCurrency } from '../lib/currency';

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const { data } = await getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid={DASHBOARD.container} className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back! Here's your business overview.</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div data-testid={DASHBOARD.statsGrid} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Leads"
          value={stats?.leads_count || 0}
          icon={Users}
          subtitle={`${stats?.new_leads || 0} new leads`}
          testId={DASHBOARD.leadsCard}
        />
        <StatCard
          title="Customers"
          value={stats?.customers_count || 0}
          icon={UserCircle}
          testId={DASHBOARD.customersCard}
        />
        <StatCard
          title="Total Revenue"
          value={formatCurrency(stats?.total_revenue)}
          icon={DollarSign}
          subtitle={`${formatCurrency(stats?.pending_payments)} pending`}
          testId={DASHBOARD.revenueCard}
        />
        <StatCard
          title="Bookings"
          value={stats?.bookings_count || 0}
          icon={Calendar}
          subtitle={`${stats?.pending_bookings || 0} pending`}
          testId={DASHBOARD.bookingsCard}
        />
      </div>

      {/* Charts and Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <Card className="lg:col-span-2 border-border">
          <CardHeader>
            <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Revenue Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.monthly_revenue || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#64748b" 
                    fontSize={12}
                    tickFormatter={(value) => {
                      const date = new Date(value + '-01');
                      return date.toLocaleDateString('en-US', { month: 'short' });
                    }}
                  />
                  <YAxis 
                    stroke="#64748b" 
                    fontSize={12}
                    tickFormatter={(value) => `$${value / 1000}k`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => {
                      const date = new Date(label + '-01');
                      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#84bd0e"
                    fill="#84bd0e"
                    fillOpacity={0.1}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Leads */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Recent Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.recent_leads?.length > 0 ? (
                stats.recent_leads.map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{lead.name}</p>
                      <p className="text-xs text-slate-500">{lead.destination || 'No destination'}</p>
                    </div>
                    <Badge variant="secondary" className={getStatusClass(lead.status)}>
                      {lead.status}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">No recent leads</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#383838' }}>
                <Plane className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Visa Processing</p>
                <p className="text-2xl font-bold text-foreground font-outfit">{stats?.visa_pending || 0}</p>
                <p className="text-sm text-muted-foreground">pending applications</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#84bd0e' }}>
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Pending Bookings</p>
                <p className="text-2xl font-bold text-foreground font-outfit">{stats?.pending_bookings || 0}</p>
                <p className="text-sm text-muted-foreground">awaiting confirmation</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#383838' }}>
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Pending Payments</p>
                <p className="text-2xl font-bold text-foreground font-outfit">{formatCurrency(stats?.pending_payments)}</p>
                <p className="text-sm text-muted-foreground">to be collected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
